import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Newspaper, BookOpen, UserSearch,
  Globe, Bell, ChevronDown, Menu, X, LogOut, CheckCircle,
  Clock, AlertCircle, ExternalLink, Download, Search,
  Filter, Eye, ThumbsUp, ThumbsDown, Send, BarChart2,
  TrendingUp, ArrowUp, ArrowDown, Zap, RefreshCw,
  Mail, Star, ChevronRight, Info, Copy, Link2, Bot,
  CreditCard, User, Settings, Shield
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, BarChart, Bar
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Mock Data ─────────────────────────────────────────────────────────
const MOCK_USERS = [
  { id:"u1", name:"Carlos Mendez",   email:"carlos@miamiflowplumbing.com", business:"Miami Flow Plumbing",    plan:"growth", status:"active",   score:512, joined:"Feb 1, 2026",  city:"Miami, FL",    category:"Home Services"     },
  { id:"u2", name:"Sarah Chen",      email:"sarah@sunrisedental.com",       business:"Sunrise Dental",         plan:"basic",  status:"active",   score:388, joined:"Feb 5, 2026",  city:"Miami, FL",    category:"Health & Wellness" },
  { id:"u3", name:"David Morales",   email:"d.morales@quickfixac.com",      business:"Quick Fix A/C",          plan:"growth", status:"trialing", score:441, joined:"Feb 21, 2026", city:"Doral, FL",    category:"Home Services"     },
  { id:"u4", name:"Jennifer Walsh",  email:"jen@brickelllaw.com",           business:"Brickell Law Group",     plan:"basic",  status:"trialing", score:295, joined:"Feb 22, 2026", city:"Miami, FL",    category:"Professional Svcs" },
  { id:"u5", name:"Robert Kim",      email:"rkim@coralrealty.com",          business:"Coral Gables Realty",    plan:"growth", status:"active",   score:631, joined:"Jan 15, 2026", city:"Coral Gables", category:"Real Estate"       },
  { id:"u6", name:"Amanda Price",    email:"amanda@wynwoodbakery.com",      business:"Wynwood Artisan Bakery", plan:"basic",  status:"active",   score:344, joined:"Jan 22, 2026", city:"Miami, FL",    category:"Food & Hospitality"},
  { id:"u7", name:"Michael Torres",  email:"mike@doraltechsolutions.com",   business:"Doral Tech Solutions",   plan:"growth", status:"active",   score:578, joined:"Jan 28, 2026", city:"Doral, FL",    category:"Professional Svcs" },
  { id:"u8", name:"Lucia Fernandez", email:"lucia@homesteadgym.com",        business:"Homestead Fitness Club", plan:"basic",  status:"canceled", score:201, joined:"Dec 10, 2025", city:"Homestead, FL",category:"Health & Wellness" },
];

const MOCK_BLOG_QUEUE = [
  { id:"b1", user:"Carlos Mendez",  business:"Miami Flow Plumbing", title:"5 Signs Your Miami Home Needs Emergency Plumbing This Summer",          status:"pending",  generated:"Feb 22, 2026", words:847,  seo:92, geo_tags:["Miami","Miami-Dade"],          content_preview:"Every homeowner in Miami knows the frustration of a burst pipe on a Friday night. With South Florida's unique combination of aging infrastructure, hard water, and hurricane season, your plumbing faces challenges that most of the country never experiences. In this guide, we break down the five warning signs that Miami homeowners most commonly overlook before a plumbing emergency strikes..." },
  { id:"b2", user:"David Morales",  business:"Quick Fix A/C",       title:"Why Miami Homeowners Are Replacing Their AC Units Earlier Than Expected", status:"pending",  generated:"Feb 22, 2026", words:921,  seo:88, geo_tags:["Doral","Miami-Dade","South FL"], content_preview:"The average AC unit in Florida lasts 10-12 years, but Miami's extreme heat and humidity can cut that lifespan significantly. At Quick Fix A/C, we've noticed a troubling trend: more homeowners are calling us about units that are only 7-8 years old showing signs of premature failure. Here's what's causing it and what you can do..." },
  { id:"b3", user:"Sarah Chen",     business:"Sunrise Dental",      title:"The Miami Diet and Your Teeth: What Your Dentist Wants You to Know",     status:"approved", generated:"Feb 20, 2026", words:763,  seo:85, geo_tags:["Miami","Brickell"],            content_preview:"Miami's vibrant food scene is one of the city's greatest assets, but our love of Cuban coffee, citrus fruits, and sugary cocktails comes with a dental health cost that many residents don't realize until it's too late..." },
  { id:"b4", user:"Robert Kim",     business:"Coral Gables Realty", title:"Coral Gables Real Estate Market Report — February 2026",               status:"approved", generated:"Feb 18, 2026", words:1102, seo:94, geo_tags:["Coral Gables","Miami"],          content_preview:"The Coral Gables real estate market continues to demonstrate remarkable resilience heading into Q1 2026. Single-family home prices have increased 8.3% year-over-year, with median days on market dropping to just 18 days..." },
];

const MOCK_PR_QUEUE = [
  { id:"p1", user:"Carlos Mendez",  business:"Miami Flow Plumbing",    title:"Miami Flow Plumbing Expands Emergency Services to Cover All of Miami-Dade County",  status:"pending",   generated:"Feb 22, 2026", outlet:"EIN Presswire", paid:true,  live_url:"" },
  { id:"p2", user:"David Morales",  business:"Quick Fix A/C",          title:"Quick Fix A/C Launches 24/7 Emergency Service with 2-Hour Response Guarantee",      status:"pending",   generated:"Feb 22, 2026", outlet:"PRLog",         paid:false, live_url:"" },
  { id:"p3", user:"Sarah Chen",     business:"Sunrise Dental",         title:"Sunrise Dental Introduces Same-Day Emergency Appointments for Miami Patients",       status:"submitted", generated:"Feb 20, 2026", outlet:"OpenPR",        paid:false, live_url:"" },
  { id:"p4", user:"Robert Kim",     business:"Coral Gables Realty",    title:"Coral Gables Realty Reports Record Quarter Amid South Florida Housing Boom",         status:"published", generated:"Feb 18, 2026", outlet:"EIN Presswire", paid:true,  live_url:"https://www.einpresswire.com/article/example" },
  { id:"p5", user:"Amanda Price",   business:"Wynwood Artisan Bakery", title:"Wynwood Artisan Bakery Wins Best Local Bakery Award for Third Consecutive Year",     status:"published", generated:"Feb 15, 2026", outlet:"PRLog",         paid:false, live_url:"https://prlog.org/example" },
];

const SCORE_TREND = [
  {date:"Jan 1",  avg:410},{date:"Jan 8", avg:425},{date:"Jan 15",avg:438},
  {date:"Jan 22", avg:452},{date:"Feb 1", avg:461},{date:"Feb 8", avg:474},
  {date:"Feb 15", avg:483},{date:"Feb 23",avg:498},
];

const REVENUE_DATA = [
  {month:"Oct",revenue:299},{month:"Nov",revenue:448},{month:"Dec",revenue:747},
  {month:"Jan",revenue:1295},{month:"Feb",revenue:2093},
];

// ── Shared Components ─────────────────────────────────────────────────
const PlanBadge = ({ plan }) => (
  <span className={`text-xs font-bold px-2.5 py-1 rounded-full border capitalize ${plan==="growth" ? "text-[#1E40AF] bg-blue-50 border-blue-200" : "text-[#10B981] bg-green-50 border-green-200"}`}>{plan}</span>
);

const SBadge = ({ status }) => {
  const map = {
    active:   {l:"Active",    c:"text-[#10B981]",bg:"bg-green-50", b:"border-green-200"},
    trialing: {l:"Trial",     c:"text-[#F97316]",bg:"bg-orange-50",b:"border-orange-200"},
    canceled: {l:"Canceled",  c:"text-red-500",  bg:"bg-red-50",   b:"border-red-200"},
    pending:  {l:"Pending",   c:"text-[#F97316]",bg:"bg-orange-50",b:"border-orange-200"},
    approved: {l:"Approved",  c:"text-[#10B981]",bg:"bg-green-50", b:"border-green-200"},
    submitted:{l:"Submitted", c:"text-[#1E40AF]",bg:"bg-blue-50",  b:"border-blue-200"},
    published:{l:"Published", c:"text-[#10B981]",bg:"bg-green-50", b:"border-green-200"},
    rejected: {l:"Rejected",  c:"text-red-500",  bg:"bg-red-50",   b:"border-red-200"},
  };
  const s = map[status]||map.pending;
  return <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${s.c} ${s.bg} ${s.b}`}>{s.l}</span>;
};

const StatCard = ({ icon:Icon, label, value, sub, color, iconBg, change }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-md transition-shadow">
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs font-semibold text-slate-500">{label}</span>
      <div className={`w-9 h-9 ${iconBg} rounded-xl flex items-center justify-center`}>
        <Icon className="w-4 h-4" style={{color}}/>
      </div>
    </div>
    <div className="flex items-end justify-between">
      <div>
        <p className="text-2xl font-black text-[#0F172A] font-['Outfit']">{value}</p>
        {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
      </div>
      {change !== undefined && (
        <div className={`flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-lg ${change>=0?"bg-green-50 text-green-600":"bg-red-50 text-red-500"}`}>
          {change>=0 ? <ArrowUp className="w-3 h-3"/> : <ArrowDown className="w-3 h-3"/>}
          {Math.abs(change)}
        </div>
      )}
    </div>
  </div>
);

// ── OVERVIEW ──────────────────────────────────────────────────────────
const Overview = ({ setSection }) => {
  const active   = MOCK_USERS.filter(u=>u.status==="active").length;
  const trialing = MOCK_USERS.filter(u=>u.status==="trialing").length;
  const growth   = MOCK_USERS.filter(u=>u.plan==="growth").length;
  const mrr      = MOCK_USERS.filter(u=>u.status==="active").reduce((a,u)=>a+(u.plan==="growth"?499:149),0);
  const pBlog    = MOCK_BLOG_QUEUE.filter(b=>b.status==="pending").length;
  const pPR      = MOCK_PR_QUEUE.filter(p=>p.status==="pending").length;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-black text-[#0F172A] font-['Outfit']">Admin Overview</h2>
        <p className="text-slate-500 text-sm mt-0.5">Platform health at a glance</p>
      </div>

      {(pBlog+pPR)>0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-2xl p-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-[#F97316] flex-shrink-0"/>
            <div>
              <p className="text-sm font-bold text-[#0F172A]">Action required</p>
              <p className="text-xs text-slate-600">
                {pBlog>0 && `${pBlog} blog post${pBlog>1?"s":""} awaiting approval`}
                {pBlog>0&&pPR>0&&" · "}
                {pPR>0 && `${pPR} press release${pPR>1?"s":""} awaiting review`}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            {pBlog>0 && <button onClick={()=>setSection("blog-queue")} className="text-xs font-bold text-[#F97316] bg-white border border-orange-200 px-3 py-1.5 rounded-xl hover:bg-orange-50">Review Blogs</button>}
            {pPR>0  && <button onClick={()=>setSection("pr-queue")}   className="text-xs font-bold text-[#F97316] bg-white border border-orange-200 px-3 py-1.5 rounded-xl hover:bg-orange-50">Review PRs</button>}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users}      label="Total Users"    value={MOCK_USERS.length} sub={`${trialing} on trial`}                          color="#1E40AF" iconBg="bg-blue-100"    change={2}/>
        <StatCard icon={CreditCard} label="MRR"            value={`$${mrr.toLocaleString()}`} sub={`${growth} Growth · ${active-growth} Basic`} color="#10B981" iconBg="bg-emerald-100" change={499}/>
        <StatCard icon={BarChart2}  label="Avg Score"      value="456"               sub="across all users"                                color="#7C3AED" iconBg="bg-purple-100"  change={12}/>
        <StatCard icon={Clock}      label="Pending Review" value={pBlog+pPR}         sub={`${pBlog} blogs · ${pPR} PRs`}                   color="#F97316" iconBg="bg-orange-100"/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="text-sm font-bold text-[#0F172A] mb-4">Average Score Trend</h3>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={SCORE_TREND}>
              <defs>
                <linearGradient id="ag" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#1E40AF" stopOpacity={0.15}/>
                  <stop offset="95%" stopColor="#1E40AF" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false}/>
              <XAxis dataKey="date" tick={{fontSize:10,fill:"#94A3B8"}} axisLine={false} tickLine={false}/>
              <YAxis tick={{fontSize:10,fill:"#94A3B8"}} axisLine={false} tickLine={false} domain={[380,520]}/>
              <Tooltip contentStyle={{background:"#fff",border:"1px solid #E2E8F0",borderRadius:"12px",fontSize:"12px"}}/>
              <Area type="monotone" dataKey="avg" stroke="#1E40AF" strokeWidth={2.5} fill="url(#ag)" dot={false}/>
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="text-sm font-bold text-[#0F172A] mb-4">Monthly Revenue</h3>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={REVENUE_DATA}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false}/>
              <XAxis dataKey="month" tick={{fontSize:10,fill:"#94A3B8"}} axisLine={false} tickLine={false}/>
              <YAxis tick={{fontSize:10,fill:"#94A3B8"}} axisLine={false} tickLine={false}/>
              <Tooltip formatter={v=>[`$${v}`,"Revenue"]} contentStyle={{background:"#fff",border:"1px solid #E2E8F0",borderRadius:"12px",fontSize:"12px"}}/>
              <Bar dataKey="revenue" fill="#10B981" radius={[6,6,0,0]}/>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-sm font-bold text-[#0F172A]">Recent Users</h3>
          <button onClick={()=>setSection("users")} className="text-xs text-[#1E40AF] font-semibold flex items-center gap-1 hover:opacity-70">View all <ChevronRight className="w-3 h-3"/></button>
        </div>
        <div className="divide-y divide-slate-100">
          {MOCK_USERS.slice(0,5).map(u=>(
            <div key={u.id} className="flex items-center gap-4 px-6 py-3.5 hover:bg-slate-50 transition-colors">
              <div className="w-8 h-8 bg-[#1E40AF] rounded-full flex items-center justify-center text-white text-xs font-black flex-shrink-0">{u.name[0]}</div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#0F172A] truncate">{u.name}</p>
                <p className="text-xs text-slate-500 truncate">{u.business}</p>
              </div>
              <PlanBadge plan={u.plan}/>
              <SBadge status={u.status}/>
              <span className="text-xs font-black text-[#1E40AF] font-['Outfit'] w-10 text-right">{u.score}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ── USERS ─────────────────────────────────────────────────────────────
const AdminUsers = () => {
  const [search, setSearch]           = useState("");
  const [planFilter, setPlanFilter]   = useState("all");
  const [statusFilter, setStatus]     = useState("all");

  const filtered = MOCK_USERS.filter(u => {
    const s = search.toLowerCase();
    return (
      (u.name.toLowerCase().includes(s) || u.business.toLowerCase().includes(s) || u.email.toLowerCase().includes(s)) &&
      (planFilter==="all" || u.plan===planFilter) &&
      (statusFilter==="all" || u.status===statusFilter)
    );
  });

  const exportCSV = () => {
    const rows = [["Name","Email","Business","Plan","Status","Score","City","Category","Joined"],
      ...MOCK_USERS.map(u=>[u.name,u.email,u.business,u.plan,u.status,u.score,u.city,u.category,u.joined])];
    const blob = new Blob([rows.map(r=>r.join(",")).join("\n")],{type:"text/csv"});
    const a = document.createElement("a"); a.href=URL.createObjectURL(blob); a.download="users.csv"; a.click();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-black text-[#0F172A] font-['Outfit']">All Users</h2>
          <p className="text-slate-500 text-sm mt-0.5">{MOCK_USERS.length} total users</p>
        </div>
        <button onClick={exportCSV} className="flex items-center gap-2 text-sm font-semibold text-white bg-[#1E40AF] hover:bg-[#1E3A8A] px-4 py-2 rounded-xl transition-colors">
          <Download className="w-4 h-4"/> Export CSV
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-48">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2"/>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search users, businesses, emails..."
            className="w-full h-10 pl-9 pr-4 bg-white border border-slate-200 rounded-xl text-sm focus:border-[#1E40AF] focus:outline-none text-black placeholder-slate-400"/>
        </div>
        <select value={planFilter} onChange={e=>setPlanFilter(e.target.value)} className="h-10 px-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-600 focus:border-[#1E40AF] focus:outline-none">
          <option value="all">All Plans</option><option value="basic">Basic</option><option value="growth">Growth</option>
        </select>
        <select value={statusFilter} onChange={e=>setStatus(e.target.value)} className="h-10 px-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-600 focus:border-[#1E40AF] focus:outline-none">
          <option value="all">All Statuses</option><option value="active">Active</option><option value="trialing">Trial</option><option value="canceled">Canceled</option>
        </select>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="hidden lg:grid grid-cols-12 px-6 py-3 bg-slate-50 border-b border-slate-100 text-xs font-bold text-slate-500 uppercase tracking-wider">
          <div className="col-span-3">User / Business</div><div className="col-span-3">Email</div>
          <div className="col-span-1">Plan</div><div className="col-span-1">Status</div>
          <div className="col-span-1">Score</div><div className="col-span-2">City</div><div className="col-span-1">Joined</div>
        </div>
        <div className="divide-y divide-slate-100">
          {filtered.map(u=>(
            <div key={u.id} className="grid grid-cols-1 lg:grid-cols-12 items-center px-6 py-4 hover:bg-slate-50 transition-colors gap-1 lg:gap-0">
              <div className="lg:col-span-3 flex items-center gap-3">
                <div className="w-8 h-8 bg-[#1E40AF] rounded-full flex items-center justify-center text-white text-xs font-black flex-shrink-0">{u.name[0]}</div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#0F172A] truncate">{u.name}</p>
                  <p className="text-xs text-slate-500 truncate">{u.business}</p>
                </div>
              </div>
              <div className="lg:col-span-3"><p className="text-xs text-slate-600 truncate">{u.email}</p></div>
              <div className="lg:col-span-1"><PlanBadge plan={u.plan}/></div>
              <div className="lg:col-span-1"><SBadge status={u.status}/></div>
              <div className="lg:col-span-1"><span className="text-sm font-black text-[#1E40AF] font-['Outfit']">{u.score}</span></div>
              <div className="lg:col-span-2"><span className="text-xs text-slate-500">{u.city}</span></div>
              <div className="lg:col-span-1"><span className="text-xs text-slate-400">{u.joined.split(",")[0]}</span></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ── BLOG QUEUE ────────────────────────────────────────────────────────
const BlogQueue = () => {
  const [posts, setPosts] = useState(MOCK_BLOG_QUEUE);
  const [expanded, setExpanded] = useState(null);
  const approve = id => setPosts(p=>p.map(x=>x.id===id?{...x,status:"approved"}:x));
  const reject  = id => setPosts(p=>p.map(x=>x.id===id?{...x,status:"rejected"}:x));
  const pending  = posts.filter(p=>p.status==="pending").length;
  const approved = posts.filter(p=>p.status==="approved").length;

  const exportCSV = () => {
    const csv = ["Business,Title,Status,Words,SEO,Generated",
      ...posts.map(p=>`"${p.business}","${p.title}",${p.status},${p.words},${p.seo},${p.generated}`)].join("\n");
    const a=document.createElement("a"); a.href=URL.createObjectURL(new Blob([csv],{type:"text/csv"})); a.download="blog-queue.csv"; a.click();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-black text-[#0F172A] font-['Outfit']">Blog Post Queue</h2>
          <p className="text-slate-500 text-sm mt-0.5">{pending} pending · {approved} approved</p>
        </div>
        <button onClick={exportCSV} className="flex items-center gap-2 text-sm font-semibold text-[#1E40AF] bg-blue-50 border border-blue-100 px-4 py-2 rounded-xl hover:bg-blue-100 transition-colors">
          <Download className="w-4 h-4"/> Export
        </button>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {[{l:"Pending",p:pending,"#":"#F97316",bg:"bg-orange-50",b:"border-orange-200"},{l:"Approved",p:approved,"#":"#10B981",bg:"bg-green-50",b:"border-green-200"},{l:"Total",p:posts.length,"#":"#1E40AF",bg:"bg-blue-50",b:"border-blue-200"}].map((s,i)=>(
          <div key={i} className={`rounded-2xl border p-4 ${s.bg} ${s.b}`}>
            <p className="text-xs font-medium text-slate-500 mb-1">{s.l}</p>
            <p className="text-2xl font-black font-['Outfit']" style={{color:s["#"]}}>{s.p}</p>
          </div>
        ))}
      </div>

      <div className="space-y-3">
        {posts.map(post=>(
          <div key={post.id} className={`bg-white rounded-2xl border overflow-hidden ${post.status==="pending"?"border-orange-200":post.status==="approved"?"border-green-200":"border-red-200"}`}>
            <div className="p-5 cursor-pointer" onClick={()=>setExpanded(expanded===post.id?null:post.id)}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <span className="text-xs font-bold text-slate-500">{post.business}</span>
                    <span className="text-slate-300">·</span>
                    <span className="text-xs text-slate-400">{post.user}</span>
                    <span className="text-slate-300">·</span>
                    <span className="text-xs text-slate-400">{post.generated}</span>
                  </div>
                  <p className="text-sm font-semibold text-[#0F172A] leading-snug">{post.title}</p>
                  <div className="flex items-center gap-3 mt-2 flex-wrap">
                    <span className="text-xs text-slate-500">{post.words} words</span>
                    <span className="text-xs font-bold text-[#10B981] bg-green-50 px-2 py-0.5 rounded-full">SEO {post.seo}/100</span>
                    {post.geo_tags.map((t,i)=><span key={i} className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">{t}</span>)}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <SBadge status={post.status}/>
                  <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${expanded===post.id?"rotate-90":""}`}/>
                </div>
              </div>
            </div>
            {expanded===post.id && (
              <div className="border-t border-slate-100 p-5 bg-slate-50 space-y-4">
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Content Preview</p>
                  <p className="text-sm text-slate-600 leading-relaxed">{post.content_preview}</p>
                  <p className="text-xs text-slate-400 mt-2 italic">... full post ready for delivery</p>
                </div>
                {post.status==="pending" && (
                  <div className="flex gap-3">
                    <button onClick={()=>approve(post.id)} className="flex-1 bg-[#10B981] hover:bg-[#059669] text-white font-bold py-3 rounded-xl text-sm flex items-center justify-center gap-2 transition-colors">
                      <ThumbsUp className="w-4 h-4"/> Approve & Send to Client
                    </button>
                    <button onClick={()=>reject(post.id)} className="px-6 border border-red-200 text-red-500 hover:bg-red-50 font-bold py-3 rounded-xl text-sm flex items-center justify-center gap-2 transition-colors">
                      <ThumbsDown className="w-4 h-4"/> Reject
                    </button>
                  </div>
                )}
                {post.status==="approved" && <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#10B981]"/><p className="text-xs font-bold text-[#10B981]">Approved — client has been notified</p></div>}
                {post.status==="rejected" && <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2"><AlertCircle className="w-4 h-4 text-red-500"/><p className="text-xs font-bold text-red-500">Rejected — will be regenerated</p></div>}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ── PR QUEUE ──────────────────────────────────────────────────────────
const PRQueue = () => {
  const [prs, setPRs]       = useState(MOCK_PR_QUEUE);
  const [expanded, setExp]  = useState(null);
  const [urls, setUrls]     = useState({});
  const stepLabels  = ["AI Generated","In Review","Submitted","Published"];
  const stepColors  = ["#7C3AED","#F97316","#1E40AF","#10B981"];
  const stepKeys    = ["pending","reviewing","submitted","published"];
  const stepIdx     = s => stepKeys.indexOf(s);
  const markSubmitted = id => setPRs(p=>p.map(x=>x.id===id?{...x,status:"submitted"}:x));
  const markPublished = id => { if(!urls[id]?.trim()) return; setPRs(p=>p.map(x=>x.id===id?{...x,status:"published",live_url:urls[id]}:x)); };

  const exportCSV = () => {
    const csv = ["Business,Title,Outlet,Paid,Status,Generated,Live URL",
      ...prs.map(p=>`"${p.business}","${p.title}",${p.outlet},${p.paid},${p.status},${p.generated},"${p.live_url}"`)].join("\n");
    const a=document.createElement("a"); a.href=URL.createObjectURL(new Blob([csv],{type:"text/csv"})); a.download="pr-pipeline.csv"; a.click();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-black text-[#0F172A] font-['Outfit']">PR Pipeline</h2>
          <p className="text-slate-500 text-sm mt-0.5">Track and manage all press releases</p>
        </div>
        <button onClick={exportCSV} className="flex items-center gap-2 text-sm font-semibold text-[#1E40AF] bg-blue-50 border border-blue-100 px-4 py-2 rounded-xl hover:bg-blue-100 transition-colors">
          <Download className="w-4 h-4"/> Export
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {stepLabels.map((label,i)=>{
          const count = prs.filter(p=>p.status===stepKeys[i]).length;
          return (
            <div key={i} className="bg-white rounded-2xl border border-slate-200 p-4">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center mb-2" style={{background:stepColors[i]+"20"}}>
                <div className="w-3 h-3 rounded-full" style={{background:stepColors[i]}}/>
              </div>
              <p className="text-xs font-medium text-slate-500">{label}</p>
              <p className="text-2xl font-black font-['Outfit'] mt-0.5" style={{color:stepColors[i]}}>{count}</p>
            </div>
          );
        })}
      </div>

      <div className="space-y-3">
        {prs.map(pr=>{
          const step = stepIdx(pr.status);
          return (
            <div key={pr.id} className={`bg-white rounded-2xl border overflow-hidden ${pr.status==="pending"?"border-orange-200":pr.status==="published"?"border-green-200":"border-slate-200"}`}>
              <div className="p-5 cursor-pointer" onClick={()=>setExp(expanded===pr.id?null:pr.id)}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <span className="text-xs font-bold text-slate-500">{pr.business}</span>
                      <span className="text-slate-300">·</span>
                      <span className="text-xs text-slate-500">{pr.outlet}</span>
                      {pr.paid && <span className="text-xs font-bold text-[#F97316] bg-orange-50 border border-orange-200 px-2 py-0.5 rounded-full">Paid</span>}
                    </div>
                    <p className="text-sm font-semibold text-[#0F172A] leading-snug">{pr.title}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <SBadge status={pr.status}/>
                    <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${expanded===pr.id?"rotate-90":""}`}/>
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-1">
                  {stepLabels.map((_,i)=>(
                    <div key={i} className="flex-1 h-1.5 rounded-full" style={{background:i<=step?stepColors[i]:"#E2E8F0"}}/>
                  ))}
                </div>
              </div>

              {expanded===pr.id && (
                <div className="border-t border-slate-100 p-5 bg-slate-50 space-y-4">
                  {pr.status==="pending" && (
                    <div className="space-y-3">
                      <div className="bg-orange-50 border border-orange-200 rounded-xl p-3">
                        <p className="text-xs font-bold text-[#F97316] mb-1">Action needed</p>
                        <p className="text-xs text-slate-600">Review this AI-generated press release, then manually submit to {pr.outlet}. Once submitted, update the status below.</p>
                      </div>
                      <button onClick={()=>markSubmitted(pr.id)} className="w-full bg-[#1E40AF] hover:bg-[#1E3A8A] text-white font-bold py-3 rounded-xl text-sm flex items-center justify-center gap-2 transition-colors">
                        <Send className="w-4 h-4"/> Mark as Submitted to {pr.outlet}
                      </button>
                    </div>
                  )}
                  {pr.status==="submitted" && (
                    <div className="space-y-3">
                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                        <p className="text-xs font-bold text-[#1E40AF] mb-1">Waiting for publication</p>
                        <p className="text-xs text-slate-600">Submitted to {pr.outlet}. Once live, paste the URL below — this triggers a win notification to the client and marks their score update as pending.</p>
                      </div>
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <Link2 className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2"/>
                          <input value={urls[pr.id]||""} onChange={e=>setUrls(p=>({...p,[pr.id]:e.target.value}))}
                            placeholder="Paste live URL here..."
                            className="w-full h-11 pl-9 pr-4 bg-white border border-slate-200 rounded-xl text-sm focus:border-[#10B981] focus:outline-none text-black"/>
                        </div>
                        <button onClick={()=>markPublished(pr.id)} className="bg-[#10B981] hover:bg-[#059669] text-white font-bold px-5 py-2.5 rounded-xl text-sm flex items-center gap-2 transition-colors flex-shrink-0">
                          <CheckCircle className="w-4 h-4"/> Publish
                        </button>
                      </div>
                    </div>
                  )}
                  {pr.status==="published" && (
                    <div className="space-y-3">
                      <div className="bg-green-50 border border-green-200 rounded-xl p-3">
                        <p className="text-xs font-bold text-[#10B981] mb-1">✓ Published — win notification sent to client</p>
                        <p className="text-xs text-slate-600">The client received a win notification and their score update is pending next weekly scan.</p>
                      </div>
                      {pr.live_url && <a href={pr.live_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm font-semibold text-[#1E40AF] hover:underline"><ExternalLink className="w-4 h-4"/> View live press release</a>}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── MAIN ──────────────────────────────────────────────────────────────
const AdminDashboard = () => {
  const navigate = useNavigate();
  const [section, setSection]       = useState("overview");
  const [sidebarOpen, setSidebar]   = useState(false);
  const [accountOpen, setAccount]   = useState(false);
  const pBlog = MOCK_BLOG_QUEUE.filter(b=>b.status==="pending").length;
  const pPR   = MOCK_PR_QUEUE.filter(p=>p.status==="pending").length;

  const NAV = [
    {key:"overview",   label:"Overview",      icon:LayoutDashboard},
    {key:"users",      label:"Users",          icon:Users,     badge:MOCK_USERS.length},
    {key:"blog-queue", label:"Blog Queue",     icon:BookOpen,  badge:pBlog||null, alert:pBlog>0},
    {key:"pr-queue",   label:"PR Pipeline",    icon:Newspaper, badge:pPR||null,   alert:pPR>0},
    {key:"leads",      label:"Lead Campaigns", icon:UserSearch},
    {key:"scores",     label:"Score Monitor",  icon:BarChart2},
  ];

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="px-4 py-5 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-lg font-black text-[#0F172A] font-['Outfit']">ORDO AI</span>
          <span className="text-xs font-bold text-[#F97316] bg-orange-50 border border-orange-200 px-2 py-0.5 rounded-full">Admin</span>
        </div>
        <button onClick={()=>setSidebar(false)} className="lg:hidden text-slate-400"><X className="w-5 h-5"/></button>
      </div>
      <div className="flex-1 px-3 py-3 space-y-1">
        {NAV.map(n=>{
          const Icon=n.icon;
          return (
            <button key={n.key} onClick={()=>{setSection(n.key);setSidebar(false);}}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${section===n.key?"bg-[#1E40AF] text-white":"text-slate-600 hover:bg-slate-100"}`}>
              <Icon className={`w-4 h-4 flex-shrink-0 ${section===n.key?"text-white":"text-slate-400"}`}/>
              <span className="flex-1 text-left">{n.label}</span>
              {n.badge && <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${n.alert?"bg-red-500 text-white":section===n.key?"bg-white/20 text-white":"bg-blue-100 text-[#1E40AF]"}`}>{n.badge}</span>}
            </button>
          );
        })}
      </div>
      <div className="px-3 py-3 border-t border-slate-200">
        <button onClick={()=>navigate("/dashboard")} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors">
          <User className="w-4 h-4 text-slate-400"/> View as Client
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F1F5F9] font-['Outfit'] flex">
      <aside className="hidden lg:flex flex-col w-60 bg-white border-r border-slate-200 fixed h-full z-30">
        <SidebarContent/>
      </aside>
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-60 bg-white border-r border-slate-200 flex flex-col h-full"><SidebarContent/></div>
          <div className="flex-1 bg-black/40" onClick={()=>setSidebar(false)}/>
        </div>
      )}
      <div className="flex-1 lg:ml-60 flex flex-col min-h-screen">
        <header className="bg-white border-b border-slate-200 h-16 flex items-center justify-between px-4 sm:px-6 sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <button onClick={()=>setSidebar(true)} className="lg:hidden text-slate-600"><Menu className="w-5 h-5"/></button>
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-[#F97316]"/>
              <span className="text-sm font-bold text-[#0F172A] hidden sm:block capitalize">{section==="overview"?"Admin Dashboard":section.replace("-"," ")}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {(pBlog+pPR)>0 && (
              <div className="flex items-center gap-1.5 bg-red-50 border border-red-200 text-red-500 text-xs font-bold px-3 py-1.5 rounded-full">
                <AlertCircle className="w-3 h-3"/>{pBlog+pPR} pending
              </div>
            )}
            <div className="relative">
              <button onClick={()=>setAccount(!accountOpen)} className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-slate-100 transition-colors">
                <div className="w-7 h-7 bg-[#F97316] rounded-full flex items-center justify-center text-white text-xs font-black">A</div>
                <span className="hidden sm:block text-sm font-medium text-[#0F172A]">Admin</span>
                <ChevronDown className="w-3.5 h-3.5 text-slate-400"/>
              </button>
              {accountOpen && (
                <div className="absolute right-0 top-12 w-44 bg-white rounded-2xl border border-slate-200 shadow-xl z-50 overflow-hidden">
                  <div className="py-1">
                    <button onClick={()=>navigate("/dashboard")} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50">
                      <User className="w-4 h-4 text-slate-400"/> Client View
                    </button>
                    <div className="border-t border-slate-100 mt-1 pt-1">
                      <button onClick={()=>navigate("/login")} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50">
                        <LogOut className="w-4 h-4"/> Log out
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6">
          {section==="overview"   && <Overview setSection={setSection}/>}
          {section==="users"      && <AdminUsers/>}
          {section==="blog-queue" && <BlogQueue/>}
          {section==="pr-queue"   && <PRQueue/>}
          {!["overview","users","blog-queue","pr-queue"].includes(section) && (
            <div className="flex items-center justify-center min-h-96">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Bot className="w-8 h-8 text-[#1E40AF]"/>
                </div>
                <h3 className="text-lg font-bold text-[#0F172A] font-['Outfit'] mb-2 capitalize">{section.replace("-"," ")} Section</h3>
                <p className="text-slate-500 text-sm max-w-xs">Being built. Check back soon.</p>
                <button onClick={()=>setSection("overview")} className="mt-5 bg-[#1E40AF] hover:bg-[#1E3A8A] text-white font-bold px-6 py-3 rounded-xl text-sm transition-colors">Back to Overview</button>
              </div>
            </div>
          )}
        </main>
      </div>
      {accountOpen && <div className="fixed inset-0 z-10" onClick={()=>setAccount(false)}/>}
    </div>
  );
};

export default AdminDashboard;
